<template>
  <Container>
    <template #header>
      <HeaderFe />
    </template>
    <template v-slot:default>
      <div class="min-h-screen pb-20 bg-gray-50/40">
        <!-- Page header band -->
        <section class="pt-28">
          <div class="max-w-[1170px] mx-auto px-4 sm:px-8 xl:px-0">
            <div class="max-w-2xl mx-auto text-center">
              <span
                class="inline-flex items-center gap-2 px-3 py-1 mb-3 text-xs font-semibold tracking-wide uppercase rounded-full text-primary bg-primary/10"
              >
                <span
                  class="inline-block w-1.5 h-1.5 rounded-full bg-primary"
                ></span>
                Informasi
              </span>
              <h1
                class="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl font-roboto-slab"
              >
                Berita &amp; Informasi
              </h1>
              <p class="mt-4 text-base leading-relaxed text-gray-500">
                Kabar terbaru, pengumuman, dan kegiatan terkini.
              </p>

              <!-- Search -->
              <div class="relative max-w-xl mx-auto mt-8">
                <div
                  class="absolute inset-y-0 left-0 flex items-center pl-4 text-gray-400 pointer-events-none"
                >
                  <MagnifyingGlassVue class="w-5 h-5" />
                </div>
                <input
                  v-model="search"
                  type="text"
                  autocomplete="off"
                  placeholder="Cari berita..."
                  id="search"
                  class="block w-full py-3 pl-12 pr-4 text-sm text-gray-900 bg-white border border-gray-200 shadow-sm rounded-xl placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition"
                />
              </div>
            </div>
          </div>
        </section>

        <!-- Grid -->
        <section class="mt-12 sm:mt-14">
          <div class="max-w-[1170px] mx-auto px-4 sm:px-8 xl:px-0">
            <div
              v-if="paginatedNews.length > 0"
              class="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3"
            >
              <article
                v-for="item in paginatedNews"
                :key="item.id"
                class="flex flex-col overflow-hidden transition-all duration-300 bg-white border border-gray-100 shadow-sm group rounded-2xl hover:shadow-lg hover:-translate-y-1"
              >
                <Link
                  :href="`/read/${item.slug}`"
                  class="relative block overflow-hidden aspect-[16/10] bg-gray-100"
                >
                  <img
                    v-if="firstImage(item.image)"
                    :src="firstImage(item.image)"
                    :alt="item.title"
                    class="object-cover w-full h-full transition-transform duration-500 group-hover:scale-105"
                  />
                  <div
                    v-else
                    class="flex items-center justify-center w-full h-full text-gray-300"
                  >
                    <i class="text-4xl fa-regular fa-image"></i>
                  </div>
                  <span
                    v-if="item.tags && item.tags.length"
                    class="absolute px-2.5 py-1 text-xs font-semibold text-white rounded-full top-3 left-3 bg-primary/90 backdrop-blur-sm"
                  >
                    {{ item.tags[0] }}
                  </span>
                </Link>

                <div class="flex flex-col flex-1 p-5">
                  <div
                    class="flex flex-wrap items-center gap-2 mb-2 text-xs text-gray-400"
                  >
                    <span class="inline-flex items-center gap-1">
                      <i class="fa-regular fa-user"></i>
                      {{ item.author }}
                    </span>
                    <span class="w-1 h-1 rounded-full bg-gray-300"></span>
                    <span class="inline-flex items-center gap-1">
                      <i class="fa-regular fa-calendar"></i>
                      {{ item.publish_date }}
                    </span>
                  </div>

                  <h3
                    class="text-lg font-bold leading-snug text-gray-900 transition-colors line-clamp-2 group-hover:text-primary font-roboto-slab"
                  >
                    <Link :href="`/read/${item.slug}`">{{ item.title }}</Link>
                  </h3>

                  <p class="flex-1 mt-2 text-sm text-gray-500 line-clamp-3">
                    {{ excerpt(item.content) }}
                  </p>

                  <Link
                    :href="`/read/${item.slug}`"
                    class="inline-flex items-center gap-1.5 mt-4 text-sm font-semibold text-primary hover:gap-2.5 transition-all"
                  >
                    Baca selengkapnya
                    <i class="text-xs fa-solid fa-arrow-right"></i>
                  </Link>
                </div>
              </article>
            </div>

            <!-- Empty state -->
            <div
              v-else
              class="flex flex-col items-center justify-center px-4 py-20 text-center bg-white border border-gray-100 rounded-2xl"
            >
              <i
                class="mb-4 text-4xl text-gray-300 fa-regular fa-newspaper"
              ></i>
              <h3 class="mb-1 text-lg font-semibold text-gray-900">
                Berita tidak ditemukan
              </h3>
              <p class="max-w-sm text-sm text-gray-500">
                {{
                  search
                    ? `Tidak ada berita yang sesuai dengan pencarian "${search}".`
                    : "Belum ada berita yang tersedia saat ini."
                }}
              </p>
            </div>

            <!-- Pagination -->
            <nav
              v-if="totalPages > 1"
              class="flex items-center justify-center gap-1.5 mt-12"
            >
              <button
                @click="goToPage(page - 1)"
                :disabled="page === 1"
                class="inline-flex items-center justify-center w-10 h-10 text-sm text-gray-600 transition-colors bg-white border border-gray-200 rounded-lg hover:border-primary hover:text-primary disabled:opacity-40 disabled:pointer-events-none"
                aria-label="Sebelumnya"
              >
                <i class="fa-solid fa-chevron-left"></i>
              </button>
              <button
                v-for="p in totalPages"
                :key="p"
                @click="goToPage(p)"
                :class="[
                  'inline-flex items-center justify-center w-10 h-10 text-sm font-medium rounded-lg border transition-colors',
                  p === page
                    ? 'bg-primary text-white border-primary'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-primary hover:text-primary',
                ]"
              >
                {{ p }}
              </button>
              <button
                @click="goToPage(page + 1)"
                :disabled="page === totalPages"
                class="inline-flex items-center justify-center w-10 h-10 text-sm text-gray-600 transition-colors bg-white border border-gray-200 rounded-lg hover:border-primary hover:text-primary disabled:opacity-40 disabled:pointer-events-none"
                aria-label="Berikutnya"
              >
                <i class="fa-solid fa-chevron-right"></i>
              </button>
            </nav>
          </div>
        </section>
      </div>
    </template>
    <template #footer>
      <FooterFe />
    </template>
  </Container>
</template>

<script setup>
import { defineProps, ref, computed, watch } from "vue";
import { Link } from "@inertiajs/vue3";
import Container from "@/Layouts/Container.vue";
import HeaderFe from "@/Components/HeaderFe.vue";
import FooterFe from "@/Components/FooterFe.vue";
import MagnifyingGlassVue from "@/Components/MagnifyingGlass.vue";

const props = defineProps({
  newsall: {
    type: [Object, Array],
    required: true,
    default: () => [],
  },
});

const search = ref("");
const page = ref(1);
const perPage = 6; // Show 6 items per page to match the grid

// `image` is cast to an array on the model; return the first usable URL.
const firstImage = (image) => {
  if (Array.isArray(image)) return image[0] || null;
  if (typeof image === "string" && image) return image;
  return null;
};

// Strip HTML tags and collapse whitespace to produce a clean text excerpt.
const excerpt = (html, max = 140) => {
  const text = (html || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return text.length > max ? text.slice(0, max).trim() + "…" : text;
};

// Client-side filtering
const filteredNews = computed(() => {
  let data = [];

  if (Array.isArray(props.newsall)) {
    data = props.newsall;
  } else if (props.newsall && Array.isArray(props.newsall.data)) {
    data = props.newsall.data;
  } else if (props.newsall && props.newsall.data) {
    data = Object.values(props.newsall.data);
  } else {
    return [];
  }

  if (!search.value || search.value.trim() === "") {
    return data;
  }

  const q = search.value.toLowerCase().trim();

  return data.filter((item) => {
    if (!item) return false;

    const titleMatch =
      item.title &&
      typeof item.title === "string" &&
      item.title.toLowerCase().includes(q);

    const contentMatch =
      item.content &&
      typeof item.content === "string" &&
      item.content
        .replace(/<[^>]*>/g, "")
        .toLowerCase()
        .includes(q);

    const authorMatch =
      item.author &&
      typeof item.author === "string" &&
      item.author.toLowerCase().includes(q);

    const tagsMatch =
      item.tags &&
      Array.isArray(item.tags) &&
      item.tags.some(
        (tag) => tag && typeof tag === "string" && tag.toLowerCase().includes(q)
      );

    return titleMatch || contentMatch || authorMatch || tagsMatch;
  });
});

// Client-side pagination
const paginatedNews = computed(() => {
  const start = (page.value - 1) * perPage;
  return filteredNews.value.slice(start, start + perPage);
});

const totalPages = computed(() =>
  Math.ceil(filteredNews.value.length / perPage)
);

function goToPage(p) {
  if (p >= 1 && p <= totalPages.value) {
    page.value = p;
  }
}

// Reset page when search changes
watch(search, () => {
  page.value = 1;
});
</script>
